import { FC } from 'react';

import AgencyAdminAccountPage from '@service/bugo/page/AgencyAdminAccountPage';
import AgencyAdminBugoBrandAssetManagePage from '@service/bugo/page/AgencyAdminBugoBrandAssetManagePage';
import AgencyAdminSettingsPage from '@service/bugo/page/AgencyAdminSettingsPage';
import { AgencyAdminBugoStatNewPage } from '@service/bugo/page/AgencyAdminBugoStatNewPage';
import AgencyAdminFlowerPriceTablePage from '@service/bugo/page/AgencyAdminFlowerPriceTablePage';
import AgencyAdminFlowerStatPage from '@service/bugo/page/AgencyAdminFlowerStatPage';
import AgencyAdminWorkerPage from '@service/bugo/page/AgencyAdminWorkerPage';
import AgencyAdminWorkerStatPage from '@service/bugo/page/AgencyAdminWorkerStatPage';
import CourtesyAssetContentManagePage from '@service/bugo/page/CourtesyAssetContentManagePage';
import { AgencyAdminGiftDashboardPage } from '@service/giftSeller/page/AgencyAdminGiftDashboardPage';
import { AgencyAdminGiftPriceTablePage } from '@service/giftSeller/page/AgencyAdminGiftPriceTablePage';
import { AgencyAdminGiftDeliveryManagementPage } from '@service/giftSeller/page/AgencyAdminGiftDeliveryManagementPage';
import { AgencyAdminGiftEventLogPage } from '@service/giftSeller/page/AgencyAdminGiftEventLogPage';
import { AgencyAdminGiftSettlementUnifiedPage } from '@service/giftSeller/page/AgencyAdminGiftSettlementUnifiedPage';
import { useAuth } from '@shared/state/hooks/useAuth';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

import { BugoStatRouter } from './BugoStatRouter';

/**
 * 답례품 라우트 게이트 — 상조 기능설정(featureFlags.giftEnabled)이 꺼진 상조는 직접 주소로 들어와도
 * 화환 내역으로 돌려보낸다(메뉴 숨김과 짝). 브랜드 조회 전(null)에는 판정하지 않는다.
 */
const GiftFeatureGate: FC<{ children: JSX.Element }> = ({ children }) => {
  const { bugoBrandId } = useParams();
  const { userBugoBrand } = useAuth();
  if (userBugoBrand && userBugoBrand.featureFlags?.giftEnabled !== true) {
    return <Navigate to={`/bugo-brand/${bugoBrandId}/flower-stat`} replace />;
  }
  return children;
};

/**
 * 구 정산 경로(gift-settlement/delivery·coupon) → 통합 정산 페이지의 해당 탭으로 이동.
 * 중첩 Routes 안에서 상대경로 해석이 애매해질 수 있어 절대경로로 조립한다.
 */
const GiftSettlementTabRedirect: FC<{ tab: 'delivery' | 'coupon' }> = ({ tab }) => {
  const { bugoBrandId } = useParams();
  return (
    <Navigate to={`/bugo-brand/${bugoBrandId}/gift-settlement?tab=${tab}`} replace />
  );
};

const BugoBrandIdRouter: FC = () => {
  return (
    <Routes>
      <Route path="flower-stat" element={<AgencyAdminFlowerStatPage />} />
      <Route path="flower-price-table" element={<AgencyAdminFlowerPriceTablePage />} />
      <Route path="bugo-stat" element={<BugoStatRouter />} />
      <Route path="bugo-stat-new" element={<AgencyAdminBugoStatNewPage />} />
      <Route path="agency-worker-stat" element={<AgencyAdminWorkerStatPage />} />
      <Route path="account" element={<AgencyAdminAccountPage />} />
      {/* 답례품 — giftEnabled 상조만 (GiftFeatureGate) */}
      <Route
        path="gift-event-log"
        element={
          <GiftFeatureGate>
            <AgencyAdminGiftEventLogPage />
          </GiftFeatureGate>
        }
      />
      <Route
        path="gift-delivery-management"
        element={
          <GiftFeatureGate>
            <AgencyAdminGiftDeliveryManagementPage />
          </GiftFeatureGate>
        }
      />
      <Route
        path="gift-dashboard"
        element={
          <GiftFeatureGate>
            <AgencyAdminGiftDashboardPage />
          </GiftFeatureGate>
        }
      />
      {/* 정산은 배송형·쿠폰형을 한 페이지(월별 합계 + 종류별 탭)로 합쳤다.
          기존 종류별 경로는 해당 탭으로 넘긴다(북마크 보존). */}
      <Route
        path="gift-settlement"
        element={
          <GiftFeatureGate>
            <AgencyAdminGiftSettlementUnifiedPage />
          </GiftFeatureGate>
        }
      />
      <Route
        path="gift-settlement/coupon"
        element={<GiftSettlementTabRedirect tab="coupon" />}
      />
      <Route
        path="gift-settlement/delivery"
        element={<GiftSettlementTabRedirect tab="delivery" />}
      />
      <Route
        path="gift-price-table"
        element={
          <GiftFeatureGate>
            <AgencyAdminGiftPriceTablePage />
          </GiftFeatureGate>
        }
      />
      <Route path="agency-workers" element={<AgencyAdminWorkerPage />} />
      {/* 내 계정(이름·연락처·비밀번호) — 공용 AccountSettings */}
      <Route path="settings" element={<AgencyAdminSettingsPage />} />
      <Route path="asset">
        <Route path="" element={<AgencyAdminBugoBrandAssetManagePage />} />
        <Route path="courtesy" element={<CourtesyAssetContentManagePage />}></Route>
      </Route>
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

export default BugoBrandIdRouter;
